import { TableCell, TableRow, Tooltip, Typography, Stack } from '@mui/material';
import Label from '@/components/Label';
import TableRowLink from '@/components/table/TableRowLink';
import { fDateTime } from '@/utils/formatTime';
import { bookingsManagementPath } from '@/features/bookings-management/routes/paths';
import { clientManagementPath } from '@/features/users/clients/routes/paths';
import {
  formatTransactionAmount,
  formatTransactionSource,
  getTransactionStatusPresentation,
  getTransactionTypeLabel,
} from '../utils';
import { PaymentTransaction } from '../types';

const buildWalletDisplay = (wallet: PaymentTransaction['wallet']) => {
  if (!wallet) {
    return '—';
  }

  const brand = wallet.brand || wallet.name || wallet.type;
  const lastFour = wallet.last4 ? `•••• ${wallet.last4}` : '';
  const descriptor = [brand, lastFour].filter(Boolean).join(' · ');
  const country = wallet.country ? wallet.country.toUpperCase() : '';

  const tooltipLines = [
    wallet.name && wallet.name !== brand ? wallet.name : '',
    wallet.type && wallet.type !== brand ? wallet.type : '',
    country,
  ].filter(Boolean);

  const primaryText = descriptor || wallet.stripe_id || '—';

  if (!tooltipLines.length) {
    return primaryText;
  }

  return (
    <Tooltip
      title={
        <Stack spacing={0.25}>
          {tooltipLines.map((line) => (
            <Typography key={line} variant="caption">
              {line}
            </Typography>
          ))}
        </Stack>
      }
    >
      <span>{primaryText}</span>
    </Tooltip>
  );
};

type TransactionsTableRowProps = {
  row: PaymentTransaction;
};

export default function TransactionsTableRow({ row }: TransactionsTableRowProps) {
  const status = getTransactionStatusPresentation(row);
  const createdAt = row.created_at ? fDateTime(row.created_at) : '—';
  const updatedAt = row.updated_at ? fDateTime(row.updated_at) : null;
  const amount = formatTransactionAmount(row.amount ?? 0, row.currency);
  const type = getTransactionTypeLabel(row);
  const source = formatTransactionSource(row.source);

  const bookingId = row.booking?.id ?? row.booking_id;
  const bookingLink = bookingId
    ? bookingsManagementPath('view', {
        bookingId: String(bookingId),
        bookingTabSid: 'booking-sessions/today',
      })
    : null;

  const userId = row.users?.id ?? row.user_id;
  const userLink = userId
    ? clientManagementPath('edit', {
        userId: String(userId),
        tabSid: 'details',
      })
    : null;

  const userName = [row.users?.first_name, row.users?.last_name]
    .filter(Boolean)
    .join(' ')
    .trim();
  const userEmail = row.users?.email?.trim() ?? '';
  const userDisplayText = userName || userEmail || null;
  const shouldShowEmailLink = Boolean(userEmail && userName);
  return (
    <TableRow hover>
      <TableCell>{row.id}</TableCell>
      <TableCell>
        <Stack spacing={0.25}>
          <Typography variant="body2">{createdAt}</Typography>
          {updatedAt && (
            <Typography variant="caption" color="text.secondary">
              Updated {updatedAt}
            </Typography>
          )}
        </Stack>
      </TableCell>
      <TableCell>{source}</TableCell>
      <TableCell>{type}</TableCell>
      <TableCell>
        {bookingLink && bookingId ? (
          <TableRowLink
            linkText={bookingId}
            linkTo={bookingLink}
            sx={{ width: 'fit-content', ml: -0.5 }}
          />
        ) : (
          bookingId ?? '—'
        )}
      </TableCell>
      <TableCell>
        {userLink && userDisplayText ? (
          <Stack spacing={0.25} alignItems="flex-start">
            <TableRowLink
              linkText={userDisplayText}
              linkTo={userLink}
              sx={{ width: 'fit-content', ml: -0.5 }}
            />
          
            
            {shouldShowEmailLink && (
              <Typography variant="caption" color="text.secondary">
                {userEmail}
              </Typography>
            )}

          </Stack>
        ) : (
          userDisplayText ?? '—'
        )}
      </TableCell>
      <TableCell>{amount}</TableCell>
      <TableCell>
        <Label color={status.color}>{status.label}</Label>
      </TableCell>
      <TableCell>
        {row.payment_intent ? (
          <Tooltip title={row.payment_intent}>
            <Typography variant="body2" noWrap>
              {row.payment_intent}
            </Typography>
          </Tooltip>
        ) : (
          '—'
        )}
      </TableCell>
      <TableCell>{buildWalletDisplay(row.wallet)}</TableCell>
    </TableRow>
  );
}
